import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatbotNavigationHeader from '../../components/ui/ChatbotNavigationHeader';
import ChatMessage from './components/ChatMessage';
import TypingIndicator from './components/TypingIndicator';
import QuickSuggestions from './components/QuickSuggestions';
import ChatInput from './components/ChatInput';
import WelcomeMessage from './components/WelcomeMessage';
import HelpOverlay from './components/HelpOverlay';

const LegalConsultationChatbot = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [user] = useState({
    id: 1,
    name: "علی احمدی",
    email: "ali.ahmadi@example.com"
  });

  // Mock AI responses for different legal topics
  const mockResponses = {
    "حقوق کار": `بر اساس قانون کار ایران، کارگران دارای حقوق و تکالیف مشخصی هستند. برخی از مهم‌ترین حقوق کارگران عبارتند از:

• دریافت حقوق و مزایا به موقع
• بیمه اجتماعی و درمانی
• مرخصی استحقاقی سالانه
• محیط کار ایمن و بهداشتی

آیا سوال خاص‌تری در مورد حقوق کار دارید؟`,
    
    "قرارداد": `قراردادها در حقوق ایران باید دارای شرایط زیر باشند:

• رضایت طرفین
• اهلیت قانونی طرفین
• محل قرارداد مشخص و قابل انجام
• سبب مشروع

برای اعتبار قرارداد، توصیه می‌شود آن را کتبی تنظیم کرده و در صورت نیاز، تصدیق کنید.`,
    
    "خانواده": `حقوق خانواده شامل موضوعات مختلفی مانند ازدواج، طلاق، نفقه و حضانت فرزندان است.

مهم‌ترین نکات:
• ثبت رسمی ازدواج الزامی است
• نفقه زن و فرزندان بر عهده مرد است
• حضانت فرزندان دارای قوانین خاصی است

برای مشاوره دقیق‌تر، لطفاً موضوع خاص خود را بیان کنید.`,
    
    "default": `متشکرم از سوال شما. برای ارائه پاسخ دقیق‌تر، لطفاً اطلاعات بیشتری در مورد موضوع حقوقی خود ارائه دهید.

می‌توانید از موضوعات زیر سوال بپرسید:
• حقوق کار و استخدام
• قراردادها و معاملات  
• حقوق خانواده
• املاک و مستغلات
• حقوق جزایی و کیفری

چه موضوع خاصی مد نظر شماست؟`
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const generateAIResponse = (userMessage) => {
    let message = userMessage?.toLowerCase();
    
    if (message?.includes('کار') || message?.includes('استخدام') || message?.includes('حقوق')) {
      return mockResponses?.["حقوق کار"];
    } else if (message?.includes('قرارداد') || message?.includes('معامله')) {
      return mockResponses?.["قرارداد"];
    } else if (message?.includes('خانواده') || message?.includes('ازدواج') || message?.includes('طلاق')) {
      return mockResponses?.["خانواده"];
    } else {
      return mockResponses?.["default"];
    }
  };

  const handleSendMessage = async (message) => {
    // Add user message
    const userMessage = {
      id: Date.now(),
      text: message,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShowSuggestions(false);
    setIsTyping(true);

    // Simulate AI processing delay
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        text: generateAIResponse(message),
        isUser: false,
        timestamp: new Date(),
        showActions: true
      };

      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500 + Math.random() * 1000);
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(`سوال من در مورد ${suggestion} است. لطفاً راهنمایی کنید.`);
  };

  const handleQuickStart = (type) => {
    let message = "";
    switch (type) {
      case "سوال سریع":
        message = "سلام، سوال حقوقی دارم و نیاز به راهنمایی سریع دارم.";
        break;
      case "مشاوره تخصصی":
        navigate('/lawyer-dashboard');
        return;
      case "بررسی قرارداد":
        message = "می‌خواهم قرارداد خود را بررسی کنم. چه نکاتی باید در نظر بگیرم؟";
        break;
      default:
        message = "سلام، چطور می‌توانید به من کمک کنید؟";
    }
    handleSendMessage(message);
  };

  const handleFollowUp = () => {
    setShowSuggestions(true);
  };

  const handleLawyerReferral = () => {
    navigate('/lawyer-dashboard');
  };

  const handleAttachment = (file) => {
    console.log('File attached:', file?.name);
    handleSendMessage(`فایل "${file?.name}" ارسال شد. لطفاً این سند را بررسی کنید.`);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <ChatbotNavigationHeader user={user} />
      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col pt-16">
        {messages?.length === 0 ? (
          <WelcomeMessage onQuickStart={handleQuickStart} />
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
            {messages?.map((message) => (
              <ChatMessage
                key={message?.id}
                message={message?.text}
                isUser={message?.isUser}
                timestamp={message?.timestamp}
                showActions={message?.showActions}
                onFollowUp={handleFollowUp}
                onLawyerReferral={handleLawyerReferral}
              />
            ))}
            
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Quick Suggestions */}
        {showSuggestions && messages?.length > 0 && (
          <QuickSuggestions
            onSuggestionClick={handleSuggestionClick}
            isVisible={showSuggestions}
          />
        )}

        {/* Chat Input */}
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={isTyping}
          onAttachment={handleAttachment}
        />
      </main>
      {/* Help Overlay */}
      <HelpOverlay
        isVisible={showHelp}
        onClose={() => setShowHelp(false)}
      />
    </div>
  );
};

export default LegalConsultationChatbot;